import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  CalendarSystem,
  DateFormatMode,
  FormatDescriptor,
  FormatType,
  NumberFormatLocale,
} from '@finance/contracts';
import { formatValue, type FormatContext } from './index';
import { getLanguageTag, normalizeLocale } from '../../i18n/locale-registry';

function readDateFormat(): DateFormatMode | undefined {
  if (typeof document === 'undefined') return undefined;
  const raw = document.documentElement.dataset.dateFormat;
  if (raw === 'AD' || raw === 'BS' || raw === 'DUAL') return raw;
  return undefined;
}

function readNumberLocale(): NumberFormatLocale | undefined {
  if (typeof document === 'undefined') return undefined;
  const raw = document.documentElement.dataset.numberFormat;
  if (raw === 'en-NP' || raw === 'en-US' || raw === 'en-IN') return raw;
  return undefined;
}

function readTimeZone(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const raw = document.documentElement.dataset.timeZone;
  return raw || undefined;
}

export function useLocalizedFormatter() {
  const { i18n } = useTranslation();
  const [dateFormat, setDateFormat] = useState<DateFormatMode | undefined>(readDateFormat);
  const [numberLocale, setNumberLocale] = useState<string | undefined>(readNumberLocale);
  const [timeZone, setTimeZone] = useState<string | undefined>(readTimeZone);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (event: Event) => {
      const detail =
        (
          event as CustomEvent<
            Partial<{
              dateFormat?: DateFormatMode;
              numberFormat?: NumberFormatLocale;
              timeZone?: string;
            }>
          >
        ).detail ?? {};
      if (detail.dateFormat) setDateFormat(detail.dateFormat);
      if (detail.numberFormat) setNumberLocale(detail.numberFormat);
      if (detail.timeZone) setTimeZone(detail.timeZone);
    };
    window.addEventListener('finance:preferences:changed', handler);
    return () => window.removeEventListener('finance:preferences:changed', handler);
  }, []);

  const locale = normalizeLocale(i18n.resolvedLanguage ?? i18n.language) ?? 'en';
  const calendar: CalendarSystem = dateFormat === 'BS' ? 'bikram_sambat' : 'gregorian';

  const context = useMemo<FormatContext>(
    () => ({
      locale,
      calendar,
      dateFormat,
      currencyCode: 'NPR',
      timeZone: timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
      numberLocale,
    }),
    [locale, calendar, dateFormat, numberLocale, timeZone]
  );

  return {
    locale: getLanguageTag(locale),
    numberLocale: numberLocale ?? getLanguageTag(locale),
    formatValue: (value: unknown, descriptor?: FormatDescriptor) =>
      formatValue(value, descriptor, context),
    formatByType: (value: unknown, formatType: FormatType, overrides?: Partial<FormatDescriptor>) =>
      formatValue(value, { formatType, ...overrides }, context),
  };
}
